import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { onboardingApi } from '../../api/onboarding';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Eye } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { RecordDetailModal } from '../../components/RecordDetailModal';

function OnboardingDetailModal({ employeeId, onClose }: { employeeId: number; onClose: () => void }) {
  const detailQuery = useQuery({ queryKey: ['onboarding-detail', employeeId], queryFn: () => onboardingApi.getForEmployee(employeeId) });
  if (!detailQuery.data?.data) return <RecordDetailModal title="Loading…" fields={[]} onClose={onClose} />;
  const { data: checklist, tasks } = detailQuery.data;

  return (
    <RecordDetailModal
      title={`Onboarding - ${checklist.employee_name ?? ''}`}
      onClose={onClose}
      fields={[
        { label: 'Status', value: <StatusBadge status={checklist.status} label={checklist.status === 'completed' ? 'Completed' : 'In Progress'} /> },
        { label: 'Progress', value: `${checklist.completed_tasks ?? 0} / ${checklist.total_tasks ?? 0} tasks` },
      ]}
    >
      <div style={{ marginTop: '1em' }}>
        <div className="record-detail-label">Tasks</div>
        <DataTable
          rows={tasks}
          rowKey={(t) => t.id}
          emptyMessage="No tasks."
          columns={[
            { header: 'Category', render: (t) => t.category },
            { header: 'Task', render: (t) => t.task_name },
            { header: 'Responsible', render: (t) => t.responsible_name ?? '-' },
            { header: 'Due', render: (t) => t.due_date ?? '-' },
            { header: 'Status', render: (t) => <StatusBadge status={t.status} /> },
            { header: 'Notes', render: (t) => t.notes ?? '-' },
          ]}
        />
      </div>
    </RecordDetailModal>
  );
}

/** Real HR monitoring view - genuine completion percentage per employee, computed from actual task counts, not a placeholder. */
export function OnboardingPage() {
  const checklistsQuery = useQuery({ queryKey: ['onboarding-checklists'], queryFn: () => onboardingApi.listAll() });
  const [viewingEmployeeId, setViewingEmployeeId] = useState<number | null>(null);

  return (
    <div className="page">
      <Link to="/hr">&larr; Back to HR Dashboard</Link>
      <h1>Onboarding</h1>

      <section className="card">
        <h2>All Onboarding Checklists</h2>
        {checklistsQuery.data && (
          <DataTable
            rows={checklistsQuery.data.data}
            rowKey={(c) => c.id}
            emptyMessage="No onboarding checklists yet - start one from a Staff Detail page."
            columns={[
              { header: 'Employee', render: (c) => c.employee_name },
              { header: 'Staff ID', render: (c) => c.staff_id ?? '-' },
              { header: 'Progress', render: (c) => `${c.completed_tasks ?? 0} / ${c.total_tasks ?? 0} tasks` },
              { header: 'Status', render: (c) => <StatusBadge status={c.status} label={c.status === 'completed' ? 'Completed' : 'In Progress'} /> },
              { header: '', render: (c) => (
                <span className="row-actions">
                  <IconActionButton icon={Eye} label="View" onClick={() => setViewingEmployeeId(c.employee_id)} />
                  <Link to={`/hr/staff/${c.employee_id}`}>View in Staff Detail</Link>
                </span>
              ) },
            ]}
          />
        )}
      </section>

      {viewingEmployeeId !== null && <OnboardingDetailModal employeeId={viewingEmployeeId} onClose={() => setViewingEmployeeId(null)} />}
    </div>
  );
}
